/* eslint-disable max-lines */
import {ApolloCache, gql, useApolloClient, useMutation, useQuery} from "@apollo/client";
import {
  Avatar,
  AvatarAction,
  AvatarActionInput,
  ChoiceUserInputAction,
  LanguageCode,
  Challenge,
  ChallengeInput,
  MutationAddScenarioArgs,
  MutationCopyActionArgs,
  MutationDeleteActionArgs,
  MutationDeleteScenarioArgs,
  MutationUpdateAvatarActionArgs,
  MutationUpdateChildrenArgs,
  MutationUpdateChoiceUserInputActionArgs,
  MutationUpdateScenarioArgs,
  MutationUpdateSystemActionArgs,
  MutationUpdateUserInputActionArgs,
  CustomParameter,
  MutationUpdateCustomParameterArgs,
  MutationDeleteCustomParameterArgs,
  MutationAddCustomParameterArgs,
  SystemAction,
  SystemActionInput,
  UserInputAction,
  TriggerDictionaryType,
  MutationAddTriggerDictionaryArgs,
  MutationUpdateTriggerDictionaryArgs,
  MutationDeleteTriggerDictionaryArgs,
  CutWordsDictionary,
  MutationAddCutWordsDictionaryArgs,
  MutationUpdateCutWordsDictionaryArgs,
  MutationDeleteCutWordsDictionaryArgs,
  HotWordsDictionary,
  MutationAddHotWordsDictionaryArgs,
  MutationUpdateHotWordsDictionaryArgs,
  MutationDeleteHotWordsDictionaryArgs,
  ChallengeScenarioAfterAction,
  ChallengeScenarioAfterActionTarget,
  Competence,
  ResultDescription,
  MutationAddParametricResultDescriptionArgs,
  MutationAddResultDescriptionArgs,
  MutationDeleteParametricResultDescriptionArgs,
  MutationDeleteResultDescriptionArgs,
  MutationUpdateParametricResultDescriptionArgs,
  MutationUpdateResultDescriptionArgs,
  ParametricResultDescription,
  Project, RpgInstructionParseStatus
} from "../../schema";
import {useCallback} from "react";
import {MutationFunctionOptions} from "@apollo/client/react/types/types";
import {createOptimisticResponse, moveItemInCache, rewriteCacheFragment, rewriteCacheQuery} from "../../apollo/utils";
import cloneDeep from "lodash/cloneDeep";
import {Action, Scenario} from "../../types";
import {mapObject} from "../../utils";

import ChallengeCustomizeForm from "../../components/editor/ChallengeCustomizeForm";
import ChallengeAction from "../../components/editor/ChallengeAction";
import ScenarioCustomizeForm from "../../components/editor/ScenarioCustomizeForm";
import AvatarActionEditForm from "../../components/editor/actions-forms/AvatarActionEditForm";
import AvatarBubble from "../../components/editor/actions/AvatarBubble";
import UserInputBubble from "../../components/editor/actions/UserInputBubble";
import UserInputActionEditForm from "../../components/editor/actions-forms/UserInputActionEditForm";
import SystemBubble from "../../components/editor/actions/SystemBubble";
import SystemActionEditForm from "../../components/editor/actions-forms/SystemActionEditForm";
import ChoiceUserInputBubble from "../../components/editor/actions/ChoiceUserInputBubble";
import ChoiceUserInputActionEditForm from "../../components/editor/actions-forms/ChoiceUserInputActionEditForm";

export type MutationUpdateAnyActionArgs =
  | MutationUpdateAvatarActionArgs
  | MutationUpdateSystemActionArgs
  | MutationUpdateUserInputActionArgs
  | MutationUpdateChoiceUserInputActionArgs;

export type queryVars = {
  challengeId: Challenge["id"];
  language: LanguageCode;
  path: String;
};

export type queryData = {
  challenge: Challenge;
  project: Pick<Project, "id" | "name">,
  scenarios: Scenario[];
  triggerDicts: TriggerDictionaryType[];
  cutWordsDicts: CutWordsDictionary[];
  hotWordsDicts: HotWordsDictionary[];
  supportedVoices: string[];
  parametricResultDescriptions: ParametricResultDescription[];
  resultDescriptions: ResultDescription[];
};

export const query = gql`
  query EditorEditChallengeSceneQuery($challengeId: ID!, $language: LanguageCode!, $path: String) {
    challenge: getChallenge(challengeId: $challengeId, path: $path) {
      id
      name
      accessToken
      scormDownloadUrl
      contentExportUrl
      avatar {
        id
        name(languageCode: $language)
        preview
      }
      languageCode
      isInitial
      badWordsFilter
      background

      ...ChallengeActionChallenge
      ...ChallengeCustomizeForm
    }

    project: getProject(challengeId: $challengeId) {
      id
      name
    }

    scenarios: getChallengeScenarios(challengeId: $challengeId, path: $path) {
      __typename
      id
      name
      isMain
      manualMaxScore
      nextScenario
      afterActionTarget
      triggerPhrases
      afterAction
      activationMethod
      actions {
        id
        ...ChallengeAction
      }
    }

    triggerDicts: getChallengeTriggerDictionaries(challengeId: $challengeId) {
      id
      name
      phrases
    }

    cutWordsDicts: getChallengeCutWordsDictionaries(challengeId: $challengeId) {
      id
      name
      cutWordPairs
    }

    hotWordsDicts: getChallengeHotWordsDictionaries(challengeId: $challengeId) {
      id
      name
      text
    }

    resultDescriptions: getChallengeResultDescriptions(challengeId: $challengeId) {
      id
      minScore
      maxScore
      message
    }

    parametricResultDescriptions: getChallengeParametricResultDescriptions(challengeId: $challengeId) {
      id
      conditions {
        parameter {
          id
          name
        }
        competence {
          id
          name
        }
        minValue
        maxValue
      }
      message
    }

    supportedVoices: getSupportedVoices
  }

  ${ChallengeCustomizeForm.fragments.root}
  ${ChallengeAction.fragments.root}
  ${ChallengeAction.fragments.challenge}
`;

export type avatarsQueryVars = {
  language: LanguageCode;
};

export type avatarsQueryData = {
  avatars: Avatar[];
};

export const avatarsQuery = gql`
  query EditorEditChallengeSceneAvatarsQuery($language: LanguageCode!) {
    avatars: getAvatars {
      ...ChallengeCustomizeFormAvatars
    }
  }

  ${ChallengeCustomizeForm.fragments.avatars}
`;

export type competenciesQueryData = {
  competencies: Competence[];
};

export const competenciesQuery = gql`
  query EditorEditChallengeSceneCompetenciesQuery {
    competencies: getCompetencies {
      ...ChallengeCustomizeFormCompetencies
    }
  }

  ${ChallengeCustomizeForm.fragments.competencies}
`;

function moveActionInCache(
  cache: ApolloCache<any>,
  action: Pick<Action, "__typename" | "id">,
  beforeActionId: Action["id"] | null,
) {
  return moveItemInCache<Scenario, Action>(cache, action, beforeActionId, "Scenario", "actions");
}

export function useUpdateActionMutation() {
  const client = useApolloClient();

  return [
    useCallback(
      (
        type: Action["__typename"],
        opts: Omit<MutationFunctionOptions<{action: Action}, MutationUpdateAnyActionArgs>, "fetchPolicy">,
      ) => {
        const beforeActionID = opts.variables?.data?.beforeActionId;
        const actionID = opts.variables?.actionId;

        let mutation;
        let optimisticResponseAction: Action;

        switch (type) {
          case "AvatarAction": {
            mutation = updateAvatarActionMutation;

            const data = mapObject(opts.variables?.data as AvatarActionInput, (value) => value ?? null);

            const picked: Partial<AvatarAction> | undefined = data.customAudio
              ? {
                  isCustomAudio: true,
                  audio: data.customAudio,
                }
              : undefined;

            const fragmentArgs = {
              id: `${type}:${actionID}`,
              fragmentName: "UpdatedAvatarAction",
              fragment: gql`
                fragment UpdatedAvatarAction on AvatarAction {
                  ...AvatarBubble
                  ...AvatarActionEditForm
                }

                ${AvatarBubble.fragments.root}
                ${AvatarActionEditForm.fragments.root}
              `,
            };

            optimisticResponseAction = createOptimisticResponse<AvatarAction>(client, fragmentArgs, {
              updatedData: data,
              keys: ["emotion", "gesture", "media", "mediaViewRequired", "setMediaAsBackground", "text"],
              picked,
            });

            break;
          }
          case "UserInputAction": {
            mutation = updateUserInputActionMutation;

            const fragmentArgs = {
              id: `${type}:${actionID}`,
              fragmentName: "UpdatedUserInputAction",
              fragment: gql`
                fragment UpdatedUserInputAction on UserInputAction {
                  ...UserInputBubble
                  ...UserInputActionEditForm
                }

                ${UserInputBubble.fragments.root}
                ${UserInputActionEditForm.fragments.root}
              `,
            };

            const competenceId = (opts.variables?.data as any)?.competenceId;

            const updatedData: any = {
              ...opts.variables?.data,
              competence: competenceId
                ? {
                    id: competenceId,
                    name: "Optimistic Competence",
                  }
                : undefined,
            };

            optimisticResponseAction = createOptimisticResponse<UserInputAction>(client, fragmentArgs, {
              updatedData: updatedData,
              keys: ["analogTexts", "hintText", "freeSpeech", "competence", "referenceText", "questionText"],
            });
            break;
          }
          case "SystemAction": {
            mutation = updateSystemActionMutation;

            const data = mapObject(opts.variables?.data as SystemActionInput, (value) => value ?? null);

            const picked: Partial<SystemAction> | undefined = data.customAudio
              ? {
                  isCustomAudio: true,
                  audio: data.customAudio,
                }
              : undefined;

            const fragmentArgs = {
              id: `${type}:${actionID}`,
              fragmentName: "UpdatedSystemAction",
              fragment: gql`
                fragment UpdatedSystemAction on SystemAction {
                  ...SystemBubble
                  ...SystemActionEditForm
                }

                ${SystemBubble.fragments.root}
                ${SystemActionEditForm.fragments.root}
              `,
            };

            optimisticResponseAction = createOptimisticResponse<SystemAction>(client, fragmentArgs, {
              updatedData: data,
              keys: ["media", "mediaViewRequired", "setMediaAsBackground", "text"],
              picked,
            });
            break;
          }
          case "ChoiceUserInputAction": {
            mutation = updateChoiceUserInputActionMutation;

            const fragmentArgs = {
              id: `${type}:${actionID}`,
              fragmentName: "UpdatedChoiceUserInputAction",
              fragment: gql`
                fragment UpdatedChoiceUserInputAction on ChoiceUserInputAction {
                  ...ChoiceUserInputBubble
                  ...ChoiceUserInputActionEditForm
                }

                ${ChoiceUserInputBubble.fragments.root}
                ${ChoiceUserInputActionEditForm.fragments.root}
              `,
            };

            const competenceId = (opts.variables?.data as any)?.competenceId;

            const updatedData: any = {
              ...opts.variables?.data,
              competence: competenceId
                ? {
                    id: competenceId,
                    name: "Optimistic Competence",
                  }
                : undefined,
            };

            optimisticResponseAction = createOptimisticResponse<ChoiceUserInputAction>(client, fragmentArgs, {
              updatedData: updatedData, // TODO: Some mess with ChoiceUserInputActionInput type
              keys: ["isMultipleChoice", "branches", "competence", "questionText", "hintText"],
            });
            break;
          }
          default:
            throw new Error("[NotImplemented]");
        }

        return client.mutate({
          mutation,
          optimisticResponse: {
            action: optimisticResponseAction,
          },
          update: (cache, mutationResult) => {
            const action = mutationResult.data?.action;

            if (!action) {
              return;
            }

            if (beforeActionID !== undefined) {
              moveActionInCache(cache, action, beforeActionID);
              return;
            }
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}

const updateAvatarActionMutation = gql`
  mutation UpdateAvatarActionMutation($challengeId: ID!, $actionId: ID!, $data: AvatarActionInput!) {
    action: updateAvatarAction(challengeId: $challengeId, actionId: $actionId, data: $data) {
      ...AvatarBubble
      ...AvatarActionEditForm
    }
  }

  ${AvatarBubble.fragments.root}
  ${AvatarActionEditForm.fragments.root}
`;

const updateUserInputActionMutation = gql`
  mutation UpdateUserInputActionMutation($challengeId: ID!, $actionId: ID!, $data: UserInputActionInput!) {
    action: updateUserInputAction(challengeId: $challengeId, actionId: $actionId, data: $data) {
      ...UserInputBubble
      ...UserInputActionEditForm
    }
  }

  ${UserInputBubble.fragments.root}
  ${UserInputActionEditForm.fragments.root}
`;

const updateSystemActionMutation = gql`
  mutation UpdateSystemActionMutation($challengeId: ID!, $actionId: ID!, $data: SystemActionInput!) {
    action: updateSystemAction(challengeId: $challengeId, actionId: $actionId, data: $data) {
      ...SystemBubble
      ...SystemActionEditForm
    }
  }

  ${SystemBubble.fragments.root}
  ${SystemActionEditForm.fragments.root}
`;

const updateChoiceUserInputActionMutation = gql`
  mutation UpdateChoiceUserInputActionMutation($challengeId: ID!, $actionId: ID!, $data: ChoiceUserInputActionInput!) {
    action: updateChoiceUserInputAction(challengeId: $challengeId, actionId: $actionId, data: $data) {
      ...ChoiceUserInputBubble
      ...ChoiceUserInputActionEditForm
    }
  }

  ${ChoiceUserInputBubble.fragments.root}
  ${ChoiceUserInputActionEditForm.fragments.root}
`;

export function useAddActionMutation() {
  const client = useApolloClient();

  return [
    useCallback(
      (type: Action["__typename"], opts: Omit<MutationFunctionOptions<any, any>, "fetchPolicy">) => {
        const beforeActionId = opts.variables?.data?.beforeActionId;
        const scenarioId = opts.variables?.scenarioId;

        let mutation;

        switch (type) {
          case "AvatarAction":
            mutation = addAvatarActionMutation;
            break;
          case "UserInputAction":
            mutation = addUserInputActionMutation;
            break;

          case "SystemAction":
            mutation = addSystemActionMutation;
            break;

          case "ChoiceUserInputAction":
            mutation = addChoiceUserInputActionMutation;
            break;

          default:
            throw new Error("[NotImplemented]");
        }

        return client.mutate({
          mutation,
          update: (cache, mutationResult) => {
            const action = mutationResult.data?.action;

            if (!action) {
              return;
            }

            rewriteCacheFragment(
              cache,
              {
                fragment: gql`
                  fragment Scenario on Scenario {
                    id
                    __typename
                    actions {
                      id
                      __typename
                    }
                  }
                `,
                id: `Scenario:${scenarioId}`,
              },
              (
                cacheData: Pick<Scenario, "id" | "__typename"> & {
                  actions: Pick<Action, "id" | "__typename">[];
                },
              ) => {
                const updatedData = cloneDeep(cacheData);
                const updatedItems = updatedData.actions;

                if (beforeActionId) {
                  const beforeActionIndex = updatedItems.findIndex((item) => item.id === beforeActionId);

                  if (beforeActionIndex !== -1) {
                    updatedItems.splice(beforeActionIndex, 0, {
                      id: action.id,
                      __typename: action.__typename,
                    });
                  } else {
                    console.warn("cannot insert action, cause beforeActionId not found in challenge actions");
                    return undefined;
                  }
                } else {
                  updatedItems.push({
                    id: action.id,
                    __typename: action.__typename,
                  });
                }

                return updatedData;
              },
            );
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}

const addAvatarActionMutation = gql`
  mutation AddAvatarActionMutation($challengeId: ID!, $scenarioId: ID, $data: AvatarActionInput!) {
    action: addAvatarAction(challengeId: $challengeId, scenarioId: $scenarioId, data: $data) {
      id
      emotion
      gesture
      text
      audio
    }
  }
`;

const addUserInputActionMutation = gql`
  mutation AddUserInputActionMutation($challengeId: ID!, $scenarioId: ID, $data: UserInputActionInput!) {
    action: addUserInputAction(challengeId: $challengeId, scenarioId: $scenarioId, data: $data) {
      id
      questionText
      referenceText
      analogTexts
      freeSpeech
    }
  }
`;

const addSystemActionMutation = gql`
  mutation AddSystemActionMutation($challengeId: ID!, $scenarioId: ID, $data: SystemActionInput!) {
    action: addSystemAction(challengeId: $challengeId, scenarioId: $scenarioId, data: $data) {
      id
      text
      audio
    }
  }
`;

export function useCopyActionMutation() {
  const client = useApolloClient();

  return [
    useCallback(
      (opts: Omit<MutationFunctionOptions<{copyAction: boolean}, MutationCopyActionArgs>, "fetchPolicy">) => {
        const challengeId = opts.variables?.challengeId;
        const path = opts.variables?.path;

        return client.mutate({
          mutation: copyActionMutation,
          update: (cache, mutationResult) => {
            const result = mutationResult.data?.copyAction;

            if (!result) {
              return;
            }

            const updateCache = (path?: string) => {
              cache.evict({
                fieldName: "getChallengeScenarios",
                args: {challengeId, path},
              });
              cache.gc();
            };

            updateCache(path);
          },
          ...opts,
        });
      },

      [client],
    ),
  ];
}

export function useDeleteActionMutation() {
  const client = useApolloClient();

  return [
    useCallback(
      (opts: Omit<MutationFunctionOptions<{deleteAction: boolean}, MutationDeleteActionArgs>, "fetchPolicy">) => {
        const challengeId = opts.variables?.challengeId;
        const actionId = opts.variables?.actionId;
        const path = opts.variables?.path;

        return client.mutate({
          mutation: deleteActionMutation,
          update: (cache, mutationResult) => {
            const result = mutationResult.data?.deleteAction;

            if (!result) {
              return;
            }

            const updateCache = (path?: string) => {
              rewriteCacheQuery(
                cache,
                {
                  query: gql`
                    query ($challengeId: ID!, $path: String) {
                      scenarios: getChallengeScenarios(challengeId: $challengeId, path: $path) {
                        id
                        __typename
                        actions {
                          id
                          __typename
                        }
                      }
                    }
                  `,
                  variables: {challengeId, path},
                },
                (cacheData: {scenarios: Scenario[]}) => {
                  if (!cacheData.scenarios) return;

                  return {
                    scenarios: cacheData.scenarios.map((scenario) => ({
                      ...scenario,
                      actions: scenario.actions.filter((action: Action) => action.id !== actionId),
                    })),
                  };
                },
              );
            };

            updateCache(path);
          },
          ...opts,
        });
      },

      [client],
    ),
  ];
}

const copyActionMutation = gql`
  mutation CopyActionMutation($challengeId: ID!, $actionId: ID!, $path: String!) {
    copyAction(challengeId: $challengeId, actionId: $actionId, path: $path)
  }
`;

const deleteActionMutation = gql`
  mutation DeleteActionMutation($challengeId: ID!, $actionId: ID!, $path: String!) {
    deleteAction(challengeId: $challengeId, actionId: $actionId, path: $path)
  }
`;

export function useUpdateChallengeMutation() {

  return useMutation<
    {challenge: Challenge},
    {challengeId: Challenge["id"]; data: ChallengeInput; language: LanguageCode}
  >(updateChallengeMutation, {
    update(cache, _result, options) {
      const instructionFile = (options.variables?.data as any)?.instructionFile;
      const challengeId = options.variables?.challengeId;
      if (instructionFile instanceof File && challengeId) {
        cache.modify({
          id: `Challenge:${challengeId}`,
          fields: {
            rpgInstructionParseStatus: () => RpgInstructionParseStatus.IN_PROGRESS,
          },
        });
      }
    },
    refetchQueries: (mutationResult) => {
      const challengeId = mutationResult?.data?.challenge.id;

      if (!challengeId) return [];

      return [
        {
          query: gql`
            query getChallengeScenarios($challengeId: ID!) {
              scenarios: getChallengeScenarios(challengeId: $challengeId) {
                id
                __typename
                actions {
                  id
                  __typename
                  ... on ChoiceUserInputAction {
                    id
                    isMultipleChoice
                    competence {
                      id
                    }
                    branches {
                      state
                    }
                  }
                  ... on UserInputAction {
                    id
                    competence {
                      id
                    }
                  }
                }
              }
            }
          `,
          variables: {challengeId},
        },
      ];
    },
  });
}

const updateChallengeMutation = gql`
  mutation UpdateChallengeMutation($challengeId: ID!, $data: ChallengeInput!, $language: LanguageCode!) {
    challenge: updateChallenge(id: $challengeId, data: $data) {
      name
      avatar {
        id
        name(languageCode: $language)
        preview
      }
      ...ChallengeCustomizeForm
    }
  }

  ${ChallengeCustomizeForm.fragments.root}
`;

const addChoiceUserInputActionMutation = gql`
  mutation AddChoiceUserInputActionMutation($challengeId: ID!, $scenarioId: ID, $data: ChoiceUserInputActionInput!) {
    action: addChoiceUserInputAction(challengeId: $challengeId, scenarioId: $scenarioId, data: $data) {
      id
      branches {
        id
        state
        targetScenarioId
        userInput {
          id
          questionText
          referenceText
          analogTexts
          hintText
        }
        avatarReaction {
          id
          emotion
          gesture
          text
          audio
        }
        systemReaction {
          id
          text
          audio
        }
      }
    }
  }
`;

export function useAddScenarioMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{scenario: Scenario}, MutationAddScenarioArgs>, "fetchPolicy">;
  return [
    useCallback(
      (opts: opts) => {
        const challengeId = opts.variables?.challengeId;
        const path = opts.variables?.path;

        return client.mutate({
          mutation: addScenarioMutation,
          update: (cache, mutationResult) => {
            const scenario = mutationResult.data?.scenario;

            if (!scenario) {
              return;
            }

            const updateCache = (path?: string) => {
              rewriteCacheQuery(
                cache,
                {
                  query: gql`
                    query ($challengeId: ID!, $path: String) {
                      scenarios: getChallengeScenarios(challengeId: $challengeId, path: $path) {
                        id
                        __typename
                      }
                    }
                  `,
                  variables: {challengeId, path},
                },
                (cacheData: {scenarios: Scenario[]}) => {
                  if (!cacheData.scenarios) return;

                  if (cacheData.scenarios.some((s) => s.id === scenario.id)) {
                    return;
                  }

                  return {
                    scenarios: [...cacheData.scenarios, scenario],
                  };
                },
              );
            };

            updateCache(path);
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}

const addScenarioMutation = gql`
  mutation AddScenarioMutation($challengeId: ID!, $data: ScenarioInput!, $path: String!) {
    scenario: addScenario(challengeId: $challengeId, data: $data, path: $path) {
      id
      name
    }
  }
`;

export function useUpdateScenarioMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{scenario: Scenario}, MutationUpdateScenarioArgs>, "fetchPolicy">;
  return useCallback(
    (opts: opts) => {
      const {challengeId, scenarioId} = opts.variables || {};

      const readCacheData = (path?: string) => {
        return client.readQuery<{scenarios: Scenario[]}>({
          query: gql`
            query ($challengeId: ID!, $path: String) {
              scenarios: getChallengeScenarios(challengeId: $challengeId, path: $path) {
                __typename
                id
                name
                manualMaxScore
                nextScenario
                afterAction
                afterActionTarget
                activationMethod
                actions {
                  __typename
                  id
                  ... on ChoiceUserInputAction {
                    branches {
                      id
                      targetScenarioId
                    }
                  }
                }
              }
            }
          `,
          variables: {challengeId, path},
        });
      };

      const currentCacheData = readCacheData("editor");
      const cachedCurrentScenario = currentCacheData?.scenarios.find((s) => s.id === scenarioId);

      return client.mutate({
        mutation: updateScenarioMutation,
        update: (cache, mutationResult) => {
          const updatedScenario = mutationResult.data?.scenario;

          if (!updatedScenario) {
            return;
          }

          const updateCache = (path?: string) => {
            rewriteCacheQuery(
              cache,
              {
                query: gql`
                  query ($challengeId: ID!, $path: String) {
                    scenarios: getChallengeScenarios(challengeId: $challengeId, path: $path) {
                      __typename
                      id
                      name
                      manualMaxScore
                      nextScenario
                      afterAction
                      afterActionTarget
                      activationMethod
                      actions {
                        __typename
                        id
                        ... on ChoiceUserInputAction {
                          branches {
                            id
                            targetScenarioId
                          }
                        }
                      }
                    }
                  }
                `,
                variables: {challengeId, path},
              },
              (cacheData: {scenarios: Scenario[]}) => {
                if (!cacheData.scenarios) return;

                return {
                  scenarios: cacheData.scenarios.map((scenario) => {
                    if (scenario.id === scenarioId) {
                      return {
                        ...scenario,
                        ...updatedScenario,
                      };
                    }

                    const updatedActions = scenario.actions?.map((action: Action) => {
                      if (!(action as ChoiceUserInputAction).branches) {
                        return action;
                      }

                      const updatedBranches = (action as ChoiceUserInputAction).branches.map((branch) => {
                        if (
                          updatedScenario.activationMethod !== cachedCurrentScenario?.activationMethod &&
                          branch.targetScenarioId === scenarioId
                        ) {
                          return {...branch, targetScenarioId: null};
                        }
                        return branch;
                      });

                      return {...action, branches: updatedBranches};
                    });

                    return {...scenario, actions: updatedActions};
                  }),
                };
              },
            );
          };

          updateCache("editor");
        },
        ...opts,
      });
    },
    [client],
  );
}

const updateScenarioMutation = gql`
  mutation UpdateScenarioMutation($challengeId: ID!, $scenarioId: ID!, $data: ScenarioInput!, $path: String!) {
    scenario: updateScenario(challengeId: $challengeId, scenarioId: $scenarioId, data: $data, path: $path) {
      ...ScenarioCustomizeForm
    }
  }

  ${ScenarioCustomizeForm.fragments.root}
`;

const deleteScenarioMutation = gql`
  mutation DeleteScenarioMutation($challengeId: ID!, $scenarioId: ID!, $path: String!) {
    deleted: deleteScenario(challengeId: $challengeId, scenarioId: $scenarioId, path: $path)
  }
`;

export function useUpdateChildrenMutation() {
  return useMutation<{succsess: Boolean}, MutationUpdateChildrenArgs>(updateChildrenMutation);
}

const updateChildrenMutation = gql`
  mutation updateChildrenMutation($challengeId: ID) {
    success: updateChildren(challengeId: $challengeId)
  }
`;

export function useDeleteScenarioMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{deleted: boolean}, MutationDeleteScenarioArgs>, "fetchPolicy">;
  return [
    useCallback(
      (opts: opts) => {
        const challengeId = opts.variables?.challengeId;
        const scenarioId = opts.variables?.scenarioId;
        const path = opts.variables?.path;

        return client.mutate({
          mutation: deleteScenarioMutation,
          update: (cache, mutationResult) => {
            const result = mutationResult.data?.deleted;

            if (!result) {
              return;
            }

            const updateCache = (path?: string) => {
              rewriteCacheQuery(
                cache,
                {
                  query: gql`
                    query ($challengeId: ID!, $path: String) {
                      scenarios: getChallengeScenarios(challengeId: $challengeId, path: $path) {
                        id
                        nextScenario
                        afterAction
                        afterActionTarget
                        __typename
                        actions {
                          __typename
                          id
                          ... on ChoiceUserInputAction {
                            branches {
                              id
                              targetScenarioId
                            }
                          }
                        }
                      }
                    }
                  `,
                  variables: {challengeId, path},
                },
                (cacheData: {scenarios: Scenario[]}) => {
                  if (!cacheData.scenarios) return;

                  const updatedScenarios = cacheData.scenarios.map((scenario) => {
                    const updatedActions = scenario.actions?.map((action: Action) => {
                      if (!(action as ChoiceUserInputAction).branches) return action;

                      const updatedBranches = (action as ChoiceUserInputAction).branches.map((branch) => {
                        return branch.targetScenarioId === scenarioId ? {...branch, targetScenarioId: null} : branch;
                      });

                      return {...action, branches: updatedBranches};
                    });

                    return {...scenario, actions: updatedActions};
                  });

                  return {
                    scenarios: updatedScenarios
                      .filter((scenario) => scenario.id !== scenarioId)
                      .map((scenario) => ({
                        ...scenario,
                        nextScenario: scenario.nextScenario === scenarioId ? null : scenario.nextScenario,
                        afterAction:
                          scenario.nextScenario === scenarioId
                            ? ChallengeScenarioAfterAction.RETURN
                            : scenario.afterAction,
                        afterActionTarget:
                          scenario.nextScenario === scenarioId
                            ? ChallengeScenarioAfterActionTarget.MAIN
                            : scenario.afterActionTarget,
                      })),
                  };
                },
              );
            };

            updateCache(path);
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}

const addTriggerDictionaryMutation = gql`
  mutation addTriggerDictionaryMutation($data: TriggerDictionaryInput!, $challengeId: ID!) {
    dict: addTriggerDictionary(data: $data, challengeId: $challengeId) {
      id
      name
      phrases
    }
  }
`;

export function useAddTriggerDictionaryMutation() {
  return useMutation<{dict: TriggerDictionaryType}, MutationAddTriggerDictionaryArgs>(addTriggerDictionaryMutation);
}

const updateTriggerDictionaryMutation = gql`
  mutation updateTriggerDictionaryMutation(
    $data: TriggerDictionaryInput!
    $challengeId: ID!
    $triggerDictionaryId: ID!
  ) {
    dict: updateTriggerDictionary(data: $data, challengeId: $challengeId, triggerDictionaryId: $triggerDictionaryId) {
      id
      name
      phrases
    }
  }
`;

export function useUpdateTriggerDictionaryMutation() {
  return useMutation<{dict: TriggerDictionaryType}, MutationUpdateTriggerDictionaryArgs>(
    updateTriggerDictionaryMutation,
  );
}

const deleteTriggerDictionaryMutation = gql`
  mutation deleteTriggerDictionaryMutation($challengeId: ID!, $triggerDictionaryId: ID!) {
    deleted: deleteTriggerDictionary(challengeId: $challengeId, triggerDictionaryId: $triggerDictionaryId)
  }
`;

export function useDeleteTriggerDictionaryMutation() {
  return useMutation<{deleted: Boolean}, MutationDeleteTriggerDictionaryArgs>(deleteTriggerDictionaryMutation);
}

const addCutWordsDictionaryMutation = gql`
  mutation addCutWordsDictionaryMutation($data: CutWordsDictionaryInput!, $challengeId: ID!) {
    dict: addCutWordsDictionary(data: $data, challengeId: $challengeId) {
      id
      name
      cutWordPairs
    }
  }
`;

export function useAddCutWordsDictionaryMutation() {
  return useMutation<{dict: CutWordsDictionary}, MutationAddCutWordsDictionaryArgs>(addCutWordsDictionaryMutation);
}

const updateCutWordsDictionaryMutation = gql`
  mutation updateCutWordsDictionaryMutation(
    $data: CutWordsDictionaryInput!
    $challengeId: ID!
    $cutWordsDictionaryId: ID!
  ) {
    dict: updateCutWordsDictionary(
      data: $data
      challengeId: $challengeId
      cutWordsDictionaryId: $cutWordsDictionaryId
    ) {
      id
      name
      cutWordPairs
    }
  }
`;

export function useUpdateCutWordsDictionaryMutation() {
  return useMutation<{dict: CutWordsDictionary}, MutationUpdateCutWordsDictionaryArgs>(
    updateCutWordsDictionaryMutation,
  );
}

const deleteCutWordsDictionaryMutation = gql`
  mutation deleteCutWordsDictionaryMutation($challengeId: ID!, $cutWordsDictionaryId: ID!) {
    deleted: deleteCutWordsDictionary(challengeId: $challengeId, cutWordsDictionaryId: $cutWordsDictionaryId)
  }
`;

export function useDeleteCutWordsDictionaryMutation() {
  return useMutation<{deleted: Boolean}, MutationDeleteCutWordsDictionaryArgs>(deleteCutWordsDictionaryMutation);
}

const addHotWordsDictionaryMutation = gql`
  mutation AddHotWordsDictionaryMutation($data: HotWordsDictionaryInput!, $challengeId: ID!) {
    dict: addHotWordsDictionary(data: $data, challengeId: $challengeId) {
      id
      name
      text
    }
  }
`;

export function useAddHotWordsDictionaryMutation() {
  return useMutation<{dict: HotWordsDictionary}, MutationAddHotWordsDictionaryArgs>(addHotWordsDictionaryMutation);
}

const updateHotWordsDictionaryMutation = gql`
  mutation UpdateHotWordsDictionaryMutation(
    $data: HotWordsDictionaryInput!
    $challengeId: ID!
    $hotWordsDictionaryId: ID!
  ) {
    dict: updateHotWordsDictionary(
      data: $data
      challengeId: $challengeId
      hotWordsDictionaryId: $hotWordsDictionaryId
    ) {
      id
      name
      text
    }
  }
`;

export function useUpdateHotWordsDictionaryMutation() {
  return useMutation<{dict: HotWordsDictionary}, MutationUpdateHotWordsDictionaryArgs>(
    updateHotWordsDictionaryMutation,
  );
}

const deleteHotWordsDictionaryMutation = gql`
  mutation DeleteHotWordsDictionaryMutation($challengeId: ID!, $hotWordsDictionaryId: ID!) {
    deleted: deleteHotWordsDictionary(challengeId: $challengeId, hotWordsDictionaryId: $hotWordsDictionaryId)
  }
`;

export function useDeleteHotWordsDictionaryMutation() {
  return useMutation<{deleted: Boolean}, MutationDeleteHotWordsDictionaryArgs>(deleteHotWordsDictionaryMutation);
}

const addParametricResultDescriptionMutation = gql`
  mutation addParametricResultDescriptionMutation($data: ParametricResultDescriptionInput!, $challengeId: ID!) {
    description: addParametricResultDescription(data: $data, challengeId: $challengeId) {
      id
      conditions {
        parameter {
          id
          name
        }
        competence {
          id
          name
        }
        minValue
        maxValue
      }
      message
    }
  }
`;

export function useAddParametricResultDescriptionMutation() {
  return useMutation<{dict: ResultDescription}, MutationAddParametricResultDescriptionArgs>(
    addParametricResultDescriptionMutation,
  );
}

const addResultDescriptionMutation = gql`
  mutation addResultDescriptionMutation($data: ResultDescriptionInput!, $challengeId: ID!) {
    dict: addResultDescription(data: $data, challengeId: $challengeId) {
      id
      minScore
      maxScore
      message
    }
  }
`;

export function useAddResultDescriptionMutation() {
  return useMutation<{dict: ResultDescription}, MutationAddResultDescriptionArgs>(addResultDescriptionMutation);
}

const deleteParametricResultDescriptionMutation = gql`
  mutation deleteParametricResultDescriptionMutation($challengeId: ID!, $resultDescriptionId: ID!) {
    deleted: deleteParametricResultDescription(challengeId: $challengeId, resultDescriptionId: $resultDescriptionId)
  }
`;

export function useDeleteParametricResultDescriptionMutation() {
  return useMutation<{deleted: Boolean}, MutationDeleteParametricResultDescriptionArgs>(
    deleteParametricResultDescriptionMutation,
  );
}

const deleteResultDescriptionMutation = gql`
  mutation deleteResultDescriptionMutation($challengeId: ID!, $resultDescriptionId: ID!) {
    deleted: deleteResultDescription(challengeId: $challengeId, resultDescriptionId: $resultDescriptionId)
  }
`;

export function useDeleteResultDescriptionMutation() {
  return useMutation<{deleted: Boolean}, MutationDeleteResultDescriptionArgs>(deleteResultDescriptionMutation);
}

const updateParametricResultDescriptionMutation = gql`
  mutation updateParametricResultDescriptionMutation(
    $data: ParametricResultDescriptionInput!
    $challengeId: ID!
    $resultDescriptionId: ID!
  ) {
    description: updateParametricResultDescription(
      data: $data
      challengeId: $challengeId
      resultDescriptionId: $resultDescriptionId
    ) {
      id
      conditions {
        parameter {
          id
          name
        }
        competence {
          id
          name
        }
        minValue
        maxValue
      }
      message
    }
  }
`;

export function useUpdateParametricResultDescriptionMutation() {
  return useMutation<{dict: ResultDescription}, MutationUpdateParametricResultDescriptionArgs>(
    updateParametricResultDescriptionMutation,
  );
}

const updateResultDescriptionMutation = gql`
  mutation updateResultDescriptionMutation(
    $data: ResultDescriptionInput!
    $challengeId: ID!
    $resultDescriptionId: ID!
  ) {
    dict: updateResultDescription(data: $data, challengeId: $challengeId, resultDescriptionId: $resultDescriptionId) {
      id
      minScore
      maxScore
      message
    }
  }
`;

export function useUpdateResultDescriptionMutation() {
  return useMutation<{dict: ResultDescription}, MutationUpdateResultDescriptionArgs>(updateResultDescriptionMutation);
}


const getAccountCustomParametersQuery = gql`
  query GetAccountCustomParameters {
    parameters: getAccountCustomParameters {
      id
      name
    }
  }
`;

export function useGetAccountCustomParametersQuery() {
  return useQuery<{parameters: CustomParameter[]}>(getAccountCustomParametersQuery);
}

const updateCustomParameterMutation = gql`
  mutation UpdateCustomParameterMutation($parameterId: ID!, $name: String) {
    parameter: updateCustomParameter(parameterId: $parameterId, name: $name) {
      id
      name
    }
  }
`;

export function useUpdateCustomParameterMutation() {
  return useMutation<{parameter: CustomParameter}, MutationUpdateCustomParameterArgs>(
    updateCustomParameterMutation
  );
}

const deleteCustomParameterMutation = gql`
  mutation DeleteCustomParameterMutation($parameterId: ID!) {
    deleted: deleteCustomParameter(parameterId: $parameterId)
  }
`;

export function useDeleteCustomParameterMutation() {
  return useMutation<{deleted: boolean}, MutationDeleteCustomParameterArgs>(
    deleteCustomParameterMutation
  );
}

const addCustomParameterMutation = gql`
  mutation AddCustomParameterMutation($name: String!) {
    parameter: addCustomParameter(name: $name) {
      id
      name
    }
  }
`;

export function useAddCustomParameterMutation() {
  return useMutation<{parameter: CustomParameter}, MutationAddCustomParameterArgs>(
    addCustomParameterMutation
  );
}
